/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

/**
 * @file Utility for reading Server-Sent Events from the eps-assistant stream.
 */

/**
 * Reads a fetch Response body as Server-Sent Events, calling onEvent once
 * per frame as it arrives (not buffered until the stream ends).
 * @async
 * @param {Response} response fetch Response with a readable body
 * @param {(event: {type: string, data: object}) => void} onEvent called per SSE frame
 */
export const readAssistantStream = async (response, onEvent) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      parseFrame(buffer.slice(0, boundary), onEvent);
      buffer = buffer.slice(boundary + 2);
      boundary = buffer.indexOf('\n\n');
    }
  }

  if (buffer.trim()) parseFrame(buffer, onEvent);
};

/**
 * Parses a single SSE frame ("event:"/"data:" lines separated by "\n") and
 * invokes onEvent with its JSON payload. The payload's own "type" field
 * (per eps-assistant's contract) takes precedence over the SSE event line.
 * @param {string} frame raw frame text, without the trailing blank line
 * @param {(event: {type: string, data: object}) => void} onEvent called with the parsed frame
 */
const parseFrame = (frame, onEvent) => {
  let eventType = 'message';
  const dataLines = [];

  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) eventType = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }

  if (dataLines.length === 0) return;

  try {
    const data = JSON.parse(dataLines.join('\n'));
    onEvent({ type: data.type || eventType, data });
  } catch (err) {
    console.log('Failed to parse assistant stream frame', err);
  }
};
